import { CriticitySelect, PrioritySelect } from 'src/components/ApiField';
import clsx from 'clsx';
import Pill from '../Pill';
import data_ticket_state from 'src/data/ticket-state';
import React, { FC } from 'react';
import { createUseStyles } from 'src/theming';
import { TicketSearchCriteria } from 'src/interfaces/api/Ticket';
import { empty } from 'src/utils/ObjectUtils';
import TypeSelect from '../TypeSelect';
import DomainSelect from 'src/components/ApiField/DomainSelect';
import EmployeeSelect from 'src/components/ApiField/EmployeeSelect';

const useStyles = createUseStyles({
  root: {
    background: '#fcfbfc',
    borderRadius: 3,
    marginTop: 16,
    marginBottom: 16,
    padding: 15,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 8,
  },
  title: {
    textTransform: 'uppercase',
    fontWeight: 'bold',
    marginBottom: 16,
    fontSize: 12,
    color: '#a5a5a5',
  },
  statusLabel: {
    marginTop: 16,
    marginBottom: '8px !important',
  },
  filterContainer: {
    '& > div': {
      marginRight: 8,
      marginBottom: 8,
    },
  },
  pill: {
    marginRight: 10,
    marginBottom: 6,
  },
  employeeSelect: {
    minWidth: '200px !important',
  },
});

interface Props {
  searchCriteria: TicketSearchCriteria;
  onCriteriaChange: (criteria: TicketSearchCriteria) => void;
}

const SearchView: FC<Props> = ({ searchCriteria, onCriteriaChange }) => {
  const classes = useStyles();

  const handleChangeState = (newStateId: number) => {
    const isSameState = newStateId === searchCriteria.stateId;

    handleChangeCriterion({
      closed: false,
      stateId: isSameState ? undefined : newStateId,
    });
  };

  const handleSwitchClosedCriteria = () => {
    handleChangeCriterion({
      closed: !searchCriteria.closed,
      stateId: undefined,
    });
  };

  const handleChangeType = (isGlobal?: 1 | 0) => {
    onCriteriaChange({ ...searchCriteria, global: isGlobal, domainId: undefined });
  };

  const handleChangeCriteria = (fieldName: keyof TicketSearchCriteria, value: any) => {
    const newValue = empty(value) ? undefined : value;
    onCriteriaChange({ ...searchCriteria, [fieldName]: newValue });
  };

  const handleChangeCriterion = (criterion: { [key: string]: any }) => {
    onCriteriaChange({ ...searchCriteria, ...criterion });
  };

  const getEventNumberValue = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const value = event.target.value as string;
    return value ? parseInt(value) : undefined;
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>Filtrer les tickets</div>

      <div className={classes.filterContainer}>
        <TypeSelect size='small' onChange={(value) => handleChangeType(value)} value={searchCriteria.global} />

        <DomainSelect
          onChange={(event) => handleChangeCriteria('domainId', event.target.value)}
          disabled={searchCriteria.global === 1}
          value={searchCriteria.domainId}
          size='small'
        />

        <EmployeeSelect
          className={classes.employeeSelect}
          label='Employé en charge'
          value={searchCriteria.supportEmployeeId}
          onChange={(value) => handleChangeCriteria('supportEmployeeId', value)}
          size='small'
        />

        <CriticitySelect
          onChange={(event) => handleChangeCriteria('criticity', getEventNumberValue(event))}
          value={searchCriteria.criticity}
          size='small'
        />

        <PrioritySelect
          onChange={(event) => handleChangeCriteria('priority', getEventNumberValue(event))}
          value={searchCriteria.priority}
          size='small'
        />
      </div>

      <div className={clsx(classes.title, classes.statusLabel)}>Status</div>

      <div className={classes.legend}>
        {Object.entries(data_ticket_state).map(([key, data]: any) => (
          <Pill
            key={key}
            className={classes.pill}
            label={data.fr}
            active={data.value === searchCriteria.stateId}
            onClick={() => handleChangeState(data.value)}
            color={data.color}
          />
        ))}

        <Pill
          className={classes.pill}
          label='Clôturé'
          active={searchCriteria.closed}
          onClick={handleSwitchClosedCriteria}
          color='#ff0000'
        />
      </div>
    </div>
  );
};

export default SearchView;
